import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { DateUtilService } from '../../../services/dateUtil.service';
import {
  AgendamentoRealizado,
  MarmorariaAgendamento,
  MarmorariaService
} from '../../service/marmoraria.service';

type StatusVisita = 'PENDENTE' | 'EM_ANDAMENTO' | 'CONCLUIDA';

/** Convenção da aplicação para `tipoRegistro` (1 caractere no banco). */
const TIPO_ENTRADA = 'E';
const TIPO_SAIDA = 'S';

interface AgendamentoVisita {
  agendamento: MarmorariaAgendamento;
  registros: AgendamentoRealizado[];
  status: StatusVisita;
  descricao: string;
  processando: boolean;
}

interface Coordenadas {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

@Component({
  selector: 'app-visita-realizada',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './visita-realizada.component.html',
  styleUrls: ['./visita-realizada.component.css']
})
export class VisitaRealizadaComponent implements OnInit {
  private readonly marmorariaService = inject(MarmorariaService);
  private readonly authService = inject(AuthService);
  private readonly dateUtilService = inject(DateUtilService);
  private readonly toastr = inject(ToastrService);

  idFuncionario: number | null = null;
  dataSelecionada: string;

  visitas: AgendamentoVisita[] = [];
  isLoading = false;
  hasError = false;

  // Geolocalização
  posicaoAtual: Coordenadas | null = null;
  geoIndisponivel = false;
  capturandoLocalizacao = false;

  constructor() {
    this.dataSelecionada = this.dateUtilService.dataHoje;
  }

  ngOnInit(): void {
    const usuario = this.authService.getUser();
    this.idFuncionario = usuario?.idFuncionario ?? null;

    if (this.idFuncionario == null) {
      this.hasError = true;
      this.toastr.warning('Seu usuário não está vinculado a um funcionário. Não é possível carregar a agenda.');
      return;
    }

    this.capturarLocalizacao();
    this.carregarAgenda();
  }

  // ---------------------------------------------------------------------------
  // Agenda
  // ---------------------------------------------------------------------------

  carregarAgenda(): void {
    if (this.idFuncionario == null) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    this.marmorariaService.getAgendamentosPorFuncionario(this.idFuncionario, this.dataSelecionada).subscribe({
      next: (agendamentos) => {
        const ordenados = [...agendamentos].sort((a, b) =>
          (a.dataAgendamento ?? '').localeCompare(b.dataAgendamento ?? '')
        );

        this.visitas = ordenados.map((agendamento) => ({
          agendamento,
          registros: [],
          status: 'PENDENTE' as StatusVisita,
          descricao: '',
          processando: false
        }));

        this.isLoading = false;
        this.carregarRegistros();
      },
      error: () => {
        this.visitas = [];
        this.isLoading = false;
        this.hasError = true;
      }
    });
  }

  /** Carrega o histórico de registros do funcionário e associa a cada agendamento. */
  private carregarRegistros(): void {
    if (this.idFuncionario == null) {
      return;
    }

    this.marmorariaService.getAgendamentosRealizados({ idFuncionario: this.idFuncionario }).subscribe({
      next: (registros) => {
        this.visitas.forEach((visita) => {
          visita.registros = registros
            .filter((r) => r.idAgendamento === visita.agendamento.id)
            .sort((a, b) => (a.timestampCheckin ?? '').localeCompare(b.timestampCheckin ?? ''));
          visita.status = this.calcularStatus(visita.registros);
        });
      },
      error: () => {
        // Sem histórico não impede novos registros; apenas mantém tudo como pendente.
      }
    });
  }

  private calcularStatus(registros: AgendamentoRealizado[]): StatusVisita {
    const temEntrada = registros.some((r) => r.tipoRegistro === TIPO_ENTRADA);
    const temSaida = registros.some((r) => r.tipoRegistro === TIPO_SAIDA);
    if (temEntrada && temSaida) {
      return 'CONCLUIDA';
    }
    if (temEntrada) {
      return 'EM_ANDAMENTO';
    }
    return 'PENDENTE';
  }

  // ---------------------------------------------------------------------------
  // Geolocalização
  // ---------------------------------------------------------------------------

  /** Captura a posição atual do navegador e guarda em `posicaoAtual`. */
  capturarLocalizacao(): Promise<Coordenadas> {
    return new Promise<Coordenadas>((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        this.geoIndisponivel = true;
        reject(new Error('Geolocalização não suportada pelo navegador.'));
        return;
      }

      this.capturandoLocalizacao = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: Coordenadas = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          this.posicaoAtual = coords;
          this.geoIndisponivel = false;
          this.capturandoLocalizacao = false;
          resolve(coords);
        },
        (error) => {
          this.capturandoLocalizacao = false;
          this.geoIndisponivel = true;
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }

  // ---------------------------------------------------------------------------
  // Registro de check-in / check-out
  // ---------------------------------------------------------------------------

  iniciarVisita(visita: AgendamentoVisita): void {
    this.registrar(visita, TIPO_ENTRADA);
  }

  encerrarVisita(visita: AgendamentoVisita): void {
    this.registrar(visita, TIPO_SAIDA);
  }

  private async registrar(visita: AgendamentoVisita, tipoRegistro: string): Promise<void> {
    if (visita.processando || this.idFuncionario == null || visita.agendamento.id == null) {
      return;
    }

    visita.processando = true;

    let coords: Coordenadas;
    try {
      coords = await this.capturarLocalizacao();
    } catch {
      visita.processando = false;
      this.toastr.error('Não foi possível obter sua localização. Habilite o GPS/permissão de localização e tente novamente.');
      return;
    }

    const payload: AgendamentoRealizado = {
      idAgendamento: visita.agendamento.id,
      idFuncionario: this.idFuncionario,
      timestampCheckin: this.timestampAgora(),
      latitude: coords.latitude,
      longitude: coords.longitude,
      descricao: visita.descricao?.trim() || undefined,
      tipoRegistro
    };

    this.marmorariaService.createAgendamentoRealizado(payload).subscribe({
      next: (registro) => {
        visita.registros = [...visita.registros, registro].sort((a, b) =>
          (a.timestampCheckin ?? '').localeCompare(b.timestampCheckin ?? '')
        );
        visita.status = this.calcularStatus(visita.registros);
        visita.descricao = '';

        if (tipoRegistro === TIPO_SAIDA) {
          // Ao encerrar a visita, marca o agendamento como concluído (dataConclusao).
          this.concluirAgendamento(visita);
        } else {
          visita.processando = false;
          this.toastr.success('Visita iniciada com sucesso.');
        }
      },
      error: () => {
        visita.processando = false;
        this.toastr.error('Não foi possível registrar. Tente novamente.');
      }
    });
  }

  /**
   * Conclui o agendamento no backend preenchendo `dataConclusao` (PUT /agendamentos/{id}).
   * Executado após o check-out; o registro presencial já foi salvo neste ponto.
   */
  private concluirAgendamento(visita: AgendamentoVisita): void {
    const payload: MarmorariaAgendamento = {
      ...visita.agendamento,
      dataConclusao: this.timestampAgora()
    };

    this.marmorariaService.updateAgendamento(payload).subscribe({
      next: (atualizado) => {
        visita.agendamento = atualizado;
        visita.status = 'CONCLUIDA';
        visita.processando = false;
        this.toastr.success('Visita encerrada e agendamento concluído.');
      },
      error: () => {
        // O check-out foi salvo; apenas a conclusão do agendamento falhou.
        visita.processando = false;
        this.toastr.warning('Visita encerrada, mas não foi possível concluir o agendamento. Tente atualizar novamente.');
      }
    });
  }

  /** Timestamp local no formato `yyyy-MM-ddTHH:mm:ss` (sem fuso). */
  private timestampAgora(): string {
    const agora = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(agora.getDate())}` +
      `T${pad(agora.getHours())}:${pad(agora.getMinutes())}:${pad(agora.getSeconds())}`
    );
  }

  // ---------------------------------------------------------------------------
  // Helpers de exibição
  // ---------------------------------------------------------------------------

  onDataChange(): void {
    this.carregarAgenda();
  }

  podeIniciar(visita: AgendamentoVisita): boolean {
    return visita.status === 'PENDENTE' && !visita.processando;
  }

  podeEncerrar(visita: AgendamentoVisita): boolean {
    return visita.status === 'EM_ANDAMENTO' && !visita.processando;
  }

  statusLabel(status: StatusVisita): string {
    switch (status) {
      case 'EM_ANDAMENTO':
        return 'Em andamento';
      case 'CONCLUIDA':
        return 'Concluída';
      default:
        return 'Pendente';
    }
  }

  statusClasse(status: StatusVisita): string {
    switch (status) {
      case 'EM_ANDAMENTO':
        return 'status-andamento';
      case 'CONCLUIDA':
        return 'status-concluida';
      default:
        return 'status-pendente';
    }
  }

  /** Nome do cliente do agendamento, com fallback null-safe. */
  nomeCliente(visita: AgendamentoVisita): string {
    const ag = visita.agendamento;
    return ag.nomeRazaoSocial || ag.cliente?.nomeRazaoSocial || 'Cliente não informado';
  }

  /** Extrai `HH:mm` de uma data ISO sem sofrer deslocamento de fuso. */
  horaDe(dataIso: string | null | undefined): string {
    if (!dataIso) {
      return '';
    }
    const parteHora = dataIso.split('T')[1];
    return parteHora ? parteHora.substring(0, 5) : '';
  }

  tipoRegistroLabel(tipo: string): string {
    if (tipo === TIPO_ENTRADA) {
      return 'Início';
    }
    if (tipo === TIPO_SAIDA) {
      return 'Encerramento';
    }
    return tipo;
  }

  trackByVisita(_index: number, visita: AgendamentoVisita): number {
    return visita.agendamento.id;
  }
}
